import qs from 'query-string';
import React from 'react';
import { useLocation } from 'react-router-dom';

import NavigateLink from './NavigateLink';
import SavedSearchesTable from './SavedSearchesTable';
import MultiSelectCell from './TableSelect/MultiSelectCell';
import { selectAllHeader } from './TableSelect/MultiSelectHeader';
import { DOCUMENT_VIEWER, ENTITY_DISPLAY, FILE_DOWNLOAD } from '../constants/Urls';
import NavigateLinkCell from '../containers/FindPage/NavigateLinkCell';
import { useTranslation } from '../custom-hooks/useTranslation';
import HyperLinkComponent from '../form-designer/controls/ssHyperLink/HyperLinkComponent';
import { removeCurrentPageSelection } from '../utils/commonUtils';

const DocumentSearchTable = ({
  selectedRepositoryItem = {},
  selectedDocuments,
  setSelectedDocuments,
  referencedColumns,
  initialRowsPerPage,
  customFilter,
  addDocumentModal,
}) => {
  const translateString = useTranslation();
  const location = useLocation();

  const { repositoryName, queryName, nodeId } = selectedRepositoryItem;
  const searchMetadata = {
    params: {
      queryForDocRepository: true,
    },
    exportFileName: `${repositoryName} - ${queryName}`,
  };
  const rowsPerPageOptions = [10, 20, 30, 40, 50];

  if (initialRowsPerPage === 5) {
    rowsPerPageOptions.unshift(initialRowsPerPage);
  }

  const viewDocumentLink = (row) => {
    const itmId = row.ItmId;
    const link = `${DOCUMENT_VIEWER}?${qs.stringify({
      entityType: row.EntityType,
      docId: itmId,
      formEntityName: 'SDC_DOCUMENT',
      formKey: 'SDC_DOCUMENT_SHOW_REFERENCE',
      itmId,
    })}`;

    return (
      <NavigateLink to={link} target="_blank">
        {translateString('View')}
      </NavigateLink>
    );
  };

  const previewDocumentLink = (row) => {
    const params = {
      entityKey: row.EntityType,
      parId: row.ItmId,
      isDocumentContent: true,
    };

    return (
      <HyperLinkComponent
        pathname={FILE_DOWNLOAD}
        queryString={qs.stringify(params)}
        isAttachmentLink
        text={translateString('Download')}
      />
    );
  };

  // SDC_ALLOW_DOWNLOAD is '1' for true, and is '0' for false
  const shouldBeDisabled = (row) => row.SDC_ALLOW_DOWNLOAD === '0' && !addDocumentModal;

  const tooltipText = () => translateString("This document can't be downloaded");

  const handleMultiSelect = (checked, row) => {
    const newSelections = checked
      ? [...selectedDocuments, row]
      : selectedDocuments.filter((s) => s[s.PrimaryKeyColumn] !== row[row.PrimaryKeyColumn]);

    setSelectedDocuments(newSelections);
  };

  const handleHeaderMultiselect = (
    allRowsPerPage,
    isAllRecordsPerPageSelected,
    isSelectedRecordOnCurrentPage
  ) => {
    if (
      (isSelectedRecordOnCurrentPage && !isAllRecordsPerPageSelected) ||
      isAllRecordsPerPageSelected
    ) {
      const filteredSelections = removeCurrentPageSelection(allRowsPerPage, selectedDocuments);
      setSelectedDocuments(filteredSelections);
      return;
    }

    const rowsAllowedToBeSelected = allRowsPerPage.filter((row) => !shouldBeDisabled(row));
    setSelectedDocuments([...selectedDocuments, ...rowsAllowedToBeSelected]);
  };

  const extraColumns = [
    {
      index: 0,
      header: (rows) =>
        selectAllHeader(rows, handleHeaderMultiselect, selectedDocuments, shouldBeDisabled),
      accessor: 'SELECT_ALL_HEADER',
      fixedWidth: true,
      customCell: MultiSelectCell(
        selectedDocuments,
        handleMultiSelect,
        shouldBeDisabled,
        shouldBeDisabled,
        tooltipText
      ),
    },
    {
      index: 1,
      accessor: '',
      header: '',
      fixedWidth: true,
      customCell: ({ row }) => {
        const qsParams = qs.parse(location.search) || {};

        return [null, 'true', '1'].includes(qsParams.enablePreview)
          ? previewDocumentLink(row)
          : viewDocumentLink(row);
      },
    },
  ];

  return (
    <SavedSearchesTable
      title={translateString(queryName)}
      metadata={searchMetadata}
      entityName="SmartDocumentList"
      initialSearchName={nodeId}
      rowsPerPage={20}
      rowsPerPageOptions={rowsPerPageOptions}
      showSearchSelector={false}
      enableSorting
      customCell={NavigateLinkCell.forUrl(ENTITY_DISPLAY)}
      extraColumns={extraColumns}
      referencedColumns={referencedColumns}
      enableSaving
      customFilter={customFilter}
    />
  );
};

export default DocumentSearchTable;
